/**
 * ParentalControls — PIN-gated settings page for K-12 content shaping.
 *
 * Not censorship — a safe lens. Guardian sets:
 *   - Adult-mode PIN (required to switch out of kid-mode)
 *   - Era ceiling (how deep into modern/violent eras kid-mode renders)
 *   - Weird Facts / Contradictions toggles
 *   - Allowed subjects (Biographies, Monuments, etc.)
 *   - External-link confirmation (click-to-open)
 *   - Self-lock: require PIN to open THIS page
 *
 * Methodology holds in play:
 *   #3 User data is portable and exportable anytime — every preference is
 *      plainly visible and reversible
 *   #5 "platform leaves every user with a clearer head than they came in with"
 *      — kids get age-appropriate, guardians get clarity, no hidden shaping.
 */
import { useState, useEffect } from 'react';
import { parentalAPI } from '../lib/api';
import { Shield, Lock, Unlock, Check, Loader2, AlertCircle, KeyRound, Filter, ArrowLeft } from 'lucide-react';

const ERA_LABEL = {
  prehistoric: 'Prehistoric (up to 3000 BCE)',
  ancient: 'Ancient (3000 BCE – 500 BCE)',
  classical: 'Classical (500 BCE – 500 CE)',
  medieval: 'Medieval (500 – 1500 CE)',
  early_modern: 'Early Modern (1500 – 1800)',
  modern: 'Modern (1800 – today)',
};

const SUBJECT_LABEL = {
  world: 'World',
  us: 'United States',
  state: 'Your State',
  city: 'Your City',
  power: 'Power & Structures',
  bios: 'Biographies',
  weird: 'Weird Facts',
  monuments: 'Monuments & Symbols',
};

function PinKeypad({ value, onChange, onSubmit }) {
  const keys = [1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, 'del'];
  return (
    <div className="grid grid-cols-3 gap-2 max-w-[220px] mx-auto">
      {keys.map((k, i) => {
        if (k === null) return <div key={`spacer-${i}`} />;
        return (
          <button
            key={`pinkey-${k}`}
            data-testid={`parental-pin-key-${k}`}
            onClick={() => {
              if (k === 'del') onChange(value.slice(0, -1));
              else if (value.length < 4) {
                const next = value + String(k);
                onChange(next);
                if (next.length === 4 && onSubmit) onSubmit(next);
              }
            }}
            className="h-11 rounded font-hud text-base text-white/80 hover:text-[#2F8A92] hover:bg-[#2F8A92]/5 transition-all duration-300 border border-[#2F8A92]/15"
          >
            {k === 'del' ? '⌫' : k}
          </button>
        );
      })}
    </div>
  );
}

function PinDots({ length }) {
  return (
    <div className="flex justify-center gap-2 mb-4">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="w-10 h-12 border-b-2 flex items-center justify-center font-hud text-xl"
          style={{ borderColor: length > i ? '#2F8A92' : '#52525B', color: '#2F8A92' }}
        >
          {length > i ? '•' : ''}
        </div>
      ))}
    </div>
  );
}

export default function ParentalControls({ onBack }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  // Self-lock state: if require_pin_for_settings is on, user must enter PIN to view rest of page
  const [locked, setLocked] = useState(false);
  const [unlockPin, setUnlockPin] = useState('');

  // PIN-change sub-flow
  const [changingPin, setChangingPin] = useState(false);
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinStep, setPinStep] = useState('new'); // new | confirm | current

  const load = async () => {
    setLoading(true);
    try {
      const r = await parentalAPI.get();
      setSettings(r.data);
      // If require_pin_for_settings is on AND a real PIN is set, start locked.
      if (r.data.filters?.require_pin_for_settings && r.data.pin_set) {
        setLocked(true);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const tryUnlock = async (pin) => {
    try {
      const r = await parentalAPI.verifyPin(pin);
      if (r.data.valid) {
        setLocked(false);
        setUnlockPin('');
        setError('');
      } else {
        setError('Incorrect PIN.');
        setUnlockPin('');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Verify failed.');
      setUnlockPin('');
    }
  };

  const saveFilters = async (patch) => {
    setSaving(true);
    setError('');
    try {
      const r = await parentalAPI.updateFilters(patch);
      setSettings((prev) => ({ ...prev, filters: { ...prev.filters, ...r.data.filters } }));
      setInfo('Saved.');
      setTimeout(() => setInfo(''), 1500);
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const toggleSubject = (subjectKey) => {
    const current = settings?.filters?.allowed_subjects || [];
    const next = current.includes(subjectKey)
      ? current.filter((s) => s !== subjectKey)
      : [...current, subjectKey];
    if (next.length === 0) {
      setError('At least one subject must stay enabled.');
      return;
    }
    saveFilters({ allowed_subjects: next });
  };

  const commitPinChange = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = { newPin };
      if (settings?.pin_set) payload.currentPin = currentPin;
      await parentalAPI.setPin(payload.newPin, payload.currentPin);
      setInfo('PIN updated.');
      setChangingPin(false);
      setCurrentPin(''); setNewPin(''); setConfirmPin(''); setPinStep('new');
      load();
      setTimeout(() => setInfo(''), 2000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not set PIN.');
      setPinStep(settings?.pin_set ? 'current' : 'new');
      setCurrentPin(''); setNewPin(''); setConfirmPin('');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="font-hud text-[#2F8A92] text-sm tracking-wider animate-pulse p-6">LOADING PARENTAL CONTROLS…</div>;
  }

  if (locked) {
    return (
      <div className="p-4 md:p-6 space-y-5 max-w-lg" data-testid="parental-locked">
        <div className="flex items-center gap-2">
          <Lock size={18} className="text-[#D4A84A]" />
          <h2 className="font-historical text-[#D4A84A] text-2xl md:text-3xl font-light tracking-tight">Parental Controls</h2>
        </div>
        <div className="border border-[#D4A84A]/20 rounded p-5 space-y-3" data-testid="parental-unlock-card">
          <p className="font-mono text-[11px] text-white/80 leading-relaxed">
            This page is locked. Enter the Adult-mode PIN to continue.
          </p>
          <PinDots length={unlockPin.length} />
          <PinKeypad
            value={unlockPin}
            onChange={setUnlockPin}
            onSubmit={(final) => tryUnlock(final)}
          />
          {error && <p className="font-mono text-[11px] text-red-400 text-center" data-testid="parental-unlock-error">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-3xl" data-testid="parental-controls">
      {onBack && (
        <button
          onClick={onBack}
          data-testid="parental-back-btn"
          className="inline-flex items-center gap-1.5 font-hud text-[10px] md:text-xs tracking-widest text-white/80 hover:text-[#2F8A92] transition-colors"
        >
          <ArrowLeft size={13} /> BACK TO MY PROFILE
        </button>
      )}
      <div>
        <h2 className="font-historical text-[#D4A84A] text-2xl md:text-3xl lg:text-4xl font-light tracking-tight flex items-center gap-2">
          <Shield size={22} /> Parental Controls
        </h2>
        <p className="font-hud text-[10px] md:text-xs text-white/80 tracking-wider mt-1">
          SAFE LENS · NOT CENSORSHIP · YOUR CALLS
        </p>
      </div>

      {info && (
        <div className="border border-[#2F8A92]/30 bg-[#2F8A92]/5 rounded p-2 font-mono text-[10px] text-[#2F8A92] flex items-center gap-2" data-testid="parental-info-banner">
          <Check size={12} /> {info}
        </div>
      )}
      {error && (
        <div className="border border-red-400/30 bg-red-400/5 rounded p-2 font-mono text-[10px] text-red-400 flex items-center gap-2" data-testid="parental-error-banner">
          <AlertCircle size={12} /> {error}
        </div>
      )}

      {/* PIN block */}
      <section className="border border-[#D4A84A]/20 rounded p-4 space-y-3" data-testid="parental-pin-section">
        <div className="flex items-center gap-2">
          <KeyRound size={14} className="text-[#D4A84A]" />
          <h3 className="font-hud text-sm tracking-widest text-[#D4A84A] uppercase">ADULT-MODE PIN</h3>
          {settings?.pin_set ? (
            <span className="font-hud text-[9px] tracking-wider px-2 py-0.5 rounded border border-[#2F8A92]/40 text-[#2F8A92]">SET</span>
          ) : (
            <span className="font-hud text-[9px] tracking-wider px-2 py-0.5 rounded border border-[#D4A84A]/40 text-[#D4A84A]">DEFAULT — CHANGE IT</span>
          )}
        </div>

        <p className="font-mono text-[11px] text-white/80 leading-relaxed">
          Kid-mode is open-access. Switching to Adult-mode requires this PIN. Default is 1234 —
          change it before handing the device to a child. We block obvious choices (0000, 1111, etc.).
        </p>

        {!changingPin ? (
          <button
            data-testid="parental-change-pin-btn"
            onClick={() => { setChangingPin(true); setPinStep(settings?.pin_set ? 'current' : 'new'); }}
            className="font-hud text-xs tracking-wider text-[#0A0A0A] bg-[#D4A84A] px-3 py-1.5 hover:bg-[#D4A84A]/85"
          >
            {settings?.pin_set ? 'CHANGE PIN' : 'SET A REAL PIN'}
          </button>
        ) : (
          <div className="space-y-2" data-testid="parental-pin-flow">
            <p className="font-hud text-[10px] tracking-wider uppercase text-[#2F8A92]">
              {pinStep === 'current' && 'ENTER CURRENT PIN'}
              {pinStep === 'new' && 'CHOOSE A NEW 4-DIGIT PIN'}
              {pinStep === 'confirm' && 'CONFIRM NEW PIN'}
            </p>
            <PinDots length={(pinStep === 'current' ? currentPin : pinStep === 'new' ? newPin : confirmPin).length} />
            <PinKeypad
              value={pinStep === 'current' ? currentPin : pinStep === 'new' ? newPin : confirmPin}
              onChange={(v) => {
                if (pinStep === 'current') setCurrentPin(v);
                else if (pinStep === 'new') setNewPin(v);
                else setConfirmPin(v);
              }}
              onSubmit={(final) => {
                if (pinStep === 'current') { setPinStep('new'); }
                else if (pinStep === 'new') { setPinStep('confirm'); }
                else {
                  if (final !== newPin) {
                    setError('PINs did not match. Try again.');
                    setConfirmPin(''); setNewPin(''); setPinStep('new');
                    return;
                  }
                  commitPinChange();
                }
              }}
            />
            <button
              onClick={() => { setChangingPin(false); setCurrentPin(''); setNewPin(''); setConfirmPin(''); setPinStep('new'); setError(''); }}
              className="font-hud text-[10px] tracking-wider text-white/80 hover:text-[#D4A84A] w-full py-1"
              data-testid="parental-pin-cancel"
            >
              CANCEL
            </button>
          </div>
        )}
      </section>

      {/* Content filters */}
      <section className="border border-[#2F8A92]/20 rounded p-4 space-y-4" data-testid="parental-filters-section">
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-[#2F8A92]" />
          <h3 className="font-hud text-sm tracking-widest text-[#2F8A92] uppercase">KID-MODE CONTENT LENS</h3>
        </div>

        <p className="font-mono text-[11px] text-white/80 leading-relaxed">
          What your child sees in Kid-mode. These filters never hide history — they shape the frame.
          Difficult content stays in the record; you decide whether it's surfaced at this age.
        </p>

        {/* Era ceiling */}
        <div className="space-y-2">
          <label className="font-hud text-[10px] tracking-widest uppercase text-white/80">ERA CEILING</label>
          <select
            data-testid="parental-era-select"
            disabled={saving}
            value={settings?.filters?.max_era || ''}
            onChange={(e) => saveFilters({ max_era: e.target.value || null })}
            className="hud-input text-sm w-full max-w-md"
          >
            <option value="">No ceiling (all eras)</option>
            {settings?.allowed_eras?.map((e) => (
              <option key={e} value={e}>{ERA_LABEL[e] || e}</option>
            ))}
          </select>
          <p className="font-mono text-[9px] text-[#52525B]">Chains whose modern-pattern step falls after the chosen era still render; era ceiling filters the kid-mode navigation surface, not the globe history.</p>
        </div>

        {/* Boolean toggles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <ToggleRow
            label="Weird Facts subject"
            testid="parental-toggle-weird"
            value={settings?.filters?.allow_weird_facts !== false}
            onChange={(v) => saveFilters({ allow_weird_facts: v })}
            help="Romans-used-pee-as-mouthwash level fun. Default: on."
          />
          <ToggleRow
            label="Show contradiction steps"
            testid="parental-toggle-contradictions"
            value={settings?.filters?.allow_contradictions !== false}
            onChange={(v) => saveFilters({ allow_contradictions: v })}
            help="The telephone-game step where narratives disagree. Default: on."
          />
          <ToggleRow
            label="Confirm before opening external links"
            testid="parental-toggle-external"
            value={!!settings?.filters?.block_external_links}
            onChange={(v) => saveFilters({ block_external_links: v })}
            help="Citation links show a 'leaving TSOGN' confirm. Default: off."
          />
          <ToggleRow
            label="Require PIN to open this page"
            testid="parental-toggle-self-lock"
            value={!!settings?.filters?.require_pin_for_settings}
            onChange={(v) => saveFilters({ require_pin_for_settings: v })}
            help="Self-lock. PIN required to re-enter Parental Controls."
          />
        </div>

        {/* Allowed subjects */}
        <div className="space-y-2">
          <label className="font-hud text-[10px] tracking-widest uppercase text-white/80">ALLOWED SUBJECTS</label>
          <div className="flex flex-wrap gap-2" data-testid="parental-allowed-subjects">
            {settings?.allowed_subjects?.map((s) => {
              const enabled = (settings.filters.allowed_subjects || []).includes(s);
              return (
                <button
                  key={s}
                  data-testid={`parental-subject-${s}`}
                  onClick={() => toggleSubject(s)}
                  className={`font-hud text-[10px] tracking-wider px-3 py-1.5 border transition-all ${
                    enabled
                      ? 'border-[#2F8A92] bg-[#2F8A92]/10 text-[#2F8A92]'
                      : 'border-[#52525B]/30 text-[#52525B] hover:text-white/80'
                  }`}
                >
                  {SUBJECT_LABEL[s] || s}
                </button>
              );
            })}
          </div>
          <p className="font-mono text-[9px] text-[#52525B]">Tap to include/exclude from Kid-mode navigation. Disabled subjects disappear from the menu but stay reachable in Adult-mode.</p>
        </div>
      </section>

      {/* Methodology footer */}
      <div className="border border-[#D4A84A]/10 rounded p-3 space-y-1">
        <p className="font-hud text-[10px] tracking-widest text-[#D4A84A] uppercase flex items-center gap-1">
          <Shield size={11} /> METHODOLOGY
        </p>
        <p className="font-mono text-[10px] text-white/80 leading-relaxed">
          Kid-mode settings are yours to set. Nothing above is hidden from the historical record — every
          era, every contradiction, every cited difficult chapter is in the archive. You're choosing
          what shows up in a child's field of view today, not what's true. They'll see the rest when
          they're ready.
        </p>
      </div>
    </div>
  );
}

function ToggleRow({ label, value, onChange, help, testid }) {
  return (
    <div className="border border-[#52525B]/20 rounded p-2 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="font-hud text-[10px] tracking-wider uppercase text-white/80">{label}</p>
        {help && <p className="font-mono text-[9px] text-[#52525B] mt-0.5">{help}</p>}
      </div>
      <button
        data-testid={testid}
        onClick={() => onChange(!value)}
        className={`font-hud text-[10px] tracking-wider px-2.5 py-1 border rounded transition-all flex-shrink-0 ${
          value ? 'border-[#2F8A92] text-[#2F8A92] bg-[#2F8A92]/5' : 'border-[#52525B]/40 text-[#52525B]'
        }`}
      >
        {value ? 'ON' : 'OFF'}
      </button>
    </div>
  );
}
